import { useEffect, useRef, useState } from "react";

import { RotateCcw } from "lucide-react";
import { useDispatch } from "react-redux";

import { startTranscriptionSummaryTask } from "@/redux/transcription-summary";
import {
  RecordingOpenElsewhereError,
  authorizeRecordingResume,
  currentSubmission,
  discardSavedOwnership,
  markRecordingSuspended,
  recoverOwnership,
  releaseRecording,
} from "@/services/recordingOwnership";
import type { store as appStore } from "@/store";
import { showError } from "@/utils/notification";

import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

import { buildSummarizeFormData } from "@pages/ReportStudio/AudioTranscriber/components/summarize-form-data";
import {
  type RecoveredSession,
  clearPersistedRecording,
  findOrphanedSession,
  formatRecoveredFeed,
  savedSessionExists,
} from "@pages/ReportStudio/AudioTranscriber/hooks/assemblyAIRecovery";
import { joinWebmRecordings } from "@pages/ReportStudio/AudioTranscriber/hooks/webmJoin";

type AssemblyAIRecoveryPromptProps = {
  // Access-control gate: only pass once the case-read query has succeeded.
  caseNumber: string | undefined;
  // Lets the page hold back RecordingRecoveryPrompt so two modals never stack.
  onSettled?: (caseNumber: string) => void;
  // Continue the interrupted recording; resolves whether it reconnected. When set, Resume is the primary action.
  onResume?: (recovered: RecoveredSession) => Promise<boolean>;
};

export const AssemblyAIRecoveryPrompt = ({
  caseNumber,
  onSettled,
  onResume,
}: AssemblyAIRecoveryPromptProps) => {
  const dispatch = useDispatch<typeof appStore.dispatch>();
  const [recovered, setRecovered] = useState<RecoveredSession | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  // Settle on unmount too, or an open prompt that unmounts would gate the Azure prompt forever.
  const pendingSettleRef = useRef<(() => void) | null>(null);
  useEffect(() => () => pendingSettleRef.current?.(), []);

  useEffect(() => {
    if (!caseNumber) return;
    let mounted = true;
    const settle = () => {
      pendingSettleRef.current = null;
      onSettled?.(caseNumber);
    };
    pendingSettleRef.current = settle;
    findOrphanedSession(caseNumber)
      .then((found) => {
        if (!mounted) return;
        setRecovered(found);
        if (!found) settle();
      })
      .catch(() => {
        if (mounted) settle();
      });
    return () => {
      mounted = false;
    };
    // Re-running when a parent re-creates onSettled would re-open a dismissed prompt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber]);

  const busy = isSending || isResuming || isDiscarding;
  // Another tab may send or discard this recording; close once it's gone from here.
  const recoveredSessionId = recovered?.sessionId;
  useEffect(() => {
    if (!recoveredSessionId || busy) return;
    let alive = true;
    let checking = false;
    const timer = setInterval(() => {
      if (checking) return;
      checking = true;
      void savedSessionExists(recoveredSessionId)
        .then((exists) => {
          if (alive && exists === false) {
            setRecovered(null);
            pendingSettleRef.current?.();
          }
        })
        .finally(() => {
          checking = false;
        });
    }, 3000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [recoveredSessionId, busy]);

  if (!recovered) return null;

  const hasAudio = !!recovered.blob;
  const feedText = recovered.feed ? formatRecoveredFeed(recovered.feed) : "";
  const hasFeed = feedText.length > 0;
  const canSend = !!recovered.examineeName && (hasAudio || hasFeed);
  // A recording the doctor stopped (its upload failed, MDRS-551) is finished, not interrupted.
  const canResume =
    !!onResume && canSend && !!recovered.submissionId && !recovered.stopped;

  const close = () => {
    setRecovered(null);
    pendingSettleRef.current?.();
  };

  // Another tab took this recording over, so this copy has nothing left to do.
  const closeIfOpenElsewhere = (error: unknown) => {
    if (!(error instanceof RecordingOpenElsewhereError)) return false;
    showError("This recording is open in another tab.", dispatch);
    close();
    return true;
  };

  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      await discardSavedOwnership(recovered.caseNumber, recovered.submissionId);
      await clearPersistedRecording(recovered.sessionId);
    } catch (error) {
      if (closeIfOpenElsewhere(error)) return;
      // The local copy is kept, so the doctor can retry or download it.
      showError("Couldn't discard the recording. Try again.", dispatch);
      return;
    } finally {
      setIsDiscarding(false);
    }
    close();
  };

  // Stays open with a spinner until connected; on failure it stays open to retry or send.
  // Not settled on success: the Azure prompt must not open over a live session.
  const handleResume = async () => {
    if (!canResume || !onResume) return;
    setIsResuming(true);
    try {
      if (!recovered.submissionId || !recovered.caseNumber)
        throw new Error("This recording cannot be resumed safely.");
      await authorizeRecordingResume(recovered.caseNumber, recovered.submissionId);
      if (await onResume(recovered)) setRecovered(null);
      else markRecordingSuspended();
    } catch (error) {
      if (closeIfOpenElsewhere(error)) return;
      markRecordingSuspended();
      // The session surfaces its own error; keep the prompt for a retry.
    } finally {
      setIsResuming(false);
    }
  };

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      if (!recovered.submissionId || !recovered.caseNumber)
        throw new Error(
          "This older recording must be downloaded before starting again."
        );
      await recoverOwnership(recovered.caseNumber, recovered.submissionId);
      const formData = buildSummarizeFormData({
        submissionId: recovered.submissionId,
        speakerHints: recovered.speakerHints,
        // Safe: findOrphanedSession only matches the definite caseNumber prop.
        caseNumber: recovered.caseNumber as string,
        examineeName: recovered.examineeName as string,
        audioBlob: recovered.blob ? await joinWebmRecordings(recovered.blob) : null,
        fallbackTranscript: feedText,
      });
      await dispatch(startTranscriptionSummaryTask(formData));
      await clearPersistedRecording(recovered.sessionId);
      releaseRecording();
      close();
    } catch (error) {
      if (closeIfOpenElsewhere(error)) return;
      // The thunk shows its own toast; keep the session so the user can retry.
      // Retrying re-claims ownership, so don't leave this tab marked busy.
      releaseRecording();
    } finally {
      setIsSending(false);
    }
  };

  const saved =
    hasAudio && hasFeed
      ? "The audio and the live transcript captured before the interruption were saved."
      : hasAudio
        ? "The audio captured before the interruption was saved."
        : "The live transcript captured before the interruption was saved (no audio survived, so a fresh speaker-labelled transcription won't be available).";

  // MDRS-551: a stopped recording whose upload failed is kept too, so it lands here.
  const interrupted = `A recording for case ${recovered.caseNumber} was interrupted or didn't finish sending for transcription — likely because of a page refresh, crash, lost connection or failed upload.`;
  const description = canResume
    ? `${interrupted} ${saved} Resume to keep recording into the same session, send what was captured for transcription, or discard it.`
    : canSend
      ? `${interrupted} ${saved} Send it for transcription now, or discard it.`
      : "A recording for this case was interrupted before it finished, and we couldn't confirm the examinee name for it, so what was captured can only be discarded.";

  return (
    <Dialog
      open={!!recovered}
      onOpenChange={(open) => {
        if (!open && !busy && currentSubmission() !== recovered?.submissionId)
          close();
      }}
    >
      <DialogContent
        className="rounded-2xl bg-background px-8 py-10"
        showCloseButton={false}
      >
        <DialogHeader className="flex flex-col items-center gap-8 text-center">
          <figure className="grid size-24 items-center justify-center rounded-full bg-primary-50">
            <RotateCcw className="size-8 text-primary" />
          </figure>
          <div>
            <DialogTitle className="text-center text-2xl font-semibold leading-relaxed">
              Recording interrupted
            </DialogTitle>
            <p className="text-center text-foreground-muted [overflow-wrap:anywhere]">
              {description}
            </p>
          </div>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-2">
          {canResume && (
            <Button
              isLoading={isResuming}
              disabled={busy}
              className="whitespace-normal"
              onClick={handleResume}
            >
              Resume recording
            </Button>
          )}
          {canSend && recovered.submissionId && (
            <Button
              variant={canResume ? "simple-outline" : "default"}
              isLoading={isSending}
              disabled={busy}
              className="whitespace-normal"
              onClick={handleSend}
            >
              Send for transcription
            </Button>
          )}
          {hasAudio && (
            <Button
              variant="simple-outline"
              disabled={busy}
              onClick={() => {
                const url = URL.createObjectURL(recovered.blob!);
                const link = document.createElement("a");
                link.href = url;
                link.download = "saved-recording.webm";
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              Download recording
            </Button>
          )}
          <Button
            variant="destructive-ghost"
            disabled={busy}
            className="whitespace-normal"
            onClick={handleDiscard}
          >
            Discard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
